import React, { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Paper,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Typography,
	IconButton,
	Switch,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Autocomplete,
	Chip
} from '@mui/material';
import { DataGrid, frFR } from '@mui/x-data-grid';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../../supabaseClient';
import { useSnackbar } from 'notistack';

function UserAdmin() {
	const { enqueueSnackbar } = useSnackbar();
	const [users, setUsers] = useState([]);
	const [roles, setRoles] = useState([]);
	const [sectors, setSectors] = useState([]);
	const [openDialog, setOpenDialog] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [userToDelete, setUserToDelete] = useState(null);
	const [selectedUser, setSelectedUser] = useState(null);
	const [formData, setFormData] = useState({
		email: '',
		full_name: '',
		is_active: true,
		role_id: '',
		sectors: []
	});

	const fetchRoles = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from('roles')
				.select('*')
				.order('name');
			if (error) throw error;
			setRoles(data);
		} catch (error) {
			console.error('Erreur lors de la récupération des rôles:', error);
			enqueueSnackbar('Erreur lors de la récupération des rôles', { variant: 'error' });
		}
	}, [enqueueSnackbar]);

	const fetchSectors = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from('sectors')
				.select('*')
				.order('name');
			if (error) throw error;
			setSectors(data);
		} catch (error) {
			console.error('Erreur lors de la récupération des secteurs:', error);
			enqueueSnackbar('Erreur lors de la récupération des secteurs', { variant: 'error' });
		}
	}, [enqueueSnackbar]);

	const fetchUsers = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from('users')
				.select(`
					*,
					user_roles (
						role_id,
						roles (
							id,
							name
						)
					),
					user_sectors (
						sector_id,
						sectors (
							id,
							name
						)
					)
				`)
				.order('created_at', { ascending: false });

			if (error) throw error;
			setUsers(data);
		} catch (error) {
			console.error('Erreur lors de la récupération des utilisateurs:', error);
			enqueueSnackbar('Erreur lors de la récupération des utilisateurs', { variant: 'error' });
		}
	}, [enqueueSnackbar]);

	useEffect(() => {
		fetchUsers();
		fetchRoles();
		fetchSectors();
	}, [fetchUsers, fetchRoles, fetchSectors]);

	const handleToggleActive = async (user) => {
		try {
			const newIsActive = !user.is_active;

			// Mettre à jour l'état local immédiatement
			setUsers(users.map(u =>
				u.id === user.id
					? { ...u, is_active: newIsActive }
					: u
			));

			const { error } = await supabase
				.from('users')
				.update({
					is_active: newIsActive,
					updated_at: new Date().toISOString()
				})
				.eq('id', user.id);

			if (error) {
				// En cas d'erreur, on revient à l'état précédent
				setUsers(users.map(u =>
					u.id === user.id
						? { ...u, is_active: user.is_active }
						: u
				));
				throw error;
			}

			enqueueSnackbar(
				`Utilisateur ${!newIsActive ? 'désactivé' : 'activé'} avec succès`,
				{ variant: 'success' }
			);
		} catch (error) {
			console.error('Erreur lors de la modification du statut:', error);
			enqueueSnackbar('Erreur lors de la modification du statut', {
				variant: 'error'
			});
		}
	};

	const handleOpenDialog = (user = null) => {
		if (user) {
			setFormData({
				email: user.email,
				full_name: user.full_name,
				is_active: user.is_active,
				role_id: user.user_roles?.[0]?.role_id || '',
				sectors: user.user_sectors?.map(us => us.sectors) || []
			});
			setSelectedUser(user);
		} else {
			setFormData({
				email: '',
				full_name: '',
				is_active: true,
				role_id: '',
				sectors: []
			});
			setSelectedUser(null);
		}
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setSelectedUser(null);
		setFormData({
			email: '',
			full_name: '',
			is_active: true,
			role_id: '',
			sectors: []
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			if (selectedUser) {
				// Mise à jour de l'utilisateur
				const { error: userError } = await supabase
					.from('users')
					.update({
						email: formData.email,
						full_name: formData.full_name,
						is_active: formData.is_active,
						updated_at: new Date().toISOString()
					})
					.eq('id', selectedUser.id);

				if (userError) throw userError;

				// Mise à jour du rôle
				await supabase
					.from('user_roles')
					.delete()
					.eq('user_id', selectedUser.id);

				if (formData.role_id) {
					const { error: roleError } = await supabase
						.from('user_roles')
						.insert({ user_id: selectedUser.id, role_id: formData.role_id });

					if (roleError) throw roleError;
				}

				// Mise à jour des secteurs - Version corrigée
				const { error: deleteError } = await supabase
					.from('user_sectors')
					.delete()
					.eq('user_id', selectedUser.id);

				if (deleteError) throw deleteError;

				if (formData.sectors.length > 0) {
					const sectorData = formData.sectors.map(sector => ({
						user_id: selectedUser.id,
						sector_id: sector.id,
						created_at: new Date().toISOString()
					}));

					const { error: sectorError } = await supabase
						.from('user_sectors')
						.insert(sectorData);

					if (sectorError) throw sectorError;
				}
			} else {
				// Création d'un nouvel utilisateur
				const { data: newUser, error: userError } = await supabase
					.from('users')
					.insert([{
						email: formData.email,
						full_name: formData.full_name,
						is_active: formData.is_active,
						created_at: new Date().toISOString(),
						updated_at: new Date().toISOString()
					}])
					.select()
					.single();

				if (userError) throw userError;

				// Ajout du rôle et des secteurs pour le nouvel utilisateur
				const promises = [];

				if (formData.role_id) {
					promises.push(
						supabase
							.from('user_roles')
							.insert({ user_id: newUser.id, role_id: formData.role_id })
					);
				}

				if (formData.sectors.length > 0) {
					const sectorData = formData.sectors.map(sector => ({
						user_id: newUser.id,
						sector_id: sector.id,
						created_at: new Date().toISOString()
					}));

					promises.push(
						supabase
							.from('user_sectors')
							.insert(sectorData)
					);
				}

				const results = await Promise.all(promises);
				const errors = results.filter(r => r.error).map(r => r.error);
				if (errors.length > 0) throw errors[0];
			}

			enqueueSnackbar(selectedUser ? 'Utilisateur modifié avec succès' : 'Utilisateur créé avec succès', {
				variant: 'success'
			});
			fetchUsers();
			handleCloseDialog();
		} catch (error) {
			console.error('Erreur lors de la sauvegarde:', error);
			enqueueSnackbar('Erreur lors de la sauvegarde', {
				variant: 'error'
			});
		}
	};

	const handleDelete = (userId) => {
		setUserToDelete(userId);
		setDeleteDialogOpen(true);
	};

	const handleConfirmDelete = async () => {
		try {
			const { error } = await supabase
				.from('users')
				.delete()
				.eq('id', userToDelete);

			if (error) {
				// En cas d'erreur de suppression, on désactive l'utilisateur
				const { error: updateError } = await supabase
					.from('users')
					.update({
						is_active: false,
						updated_at: new Date().toISOString()
					})
					.eq('id', userToDelete);

				if (updateError) throw updateError;

				enqueueSnackbar('Impossible de supprimer l\'utilisateur, il a été désactivé', {
					variant: 'warning'
				});
			} else {
				enqueueSnackbar('Utilisateur supprimé avec succès', {
					variant: 'success'
				});
			}
			fetchUsers();
		} catch (error) {
			console.error('Erreur lors de la suppression:', error);
			enqueueSnackbar('Erreur lors de la suppression', {
				variant: 'error'
			});
		} finally {
			setDeleteDialogOpen(false);
			setUserToDelete(null);
		}
	};

	const handleCancelDelete = () => {
		setDeleteDialogOpen(false);
		setUserToDelete(null);
	};

	const formatDate = (dateString) => {
		return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: fr });
	};

	const columns = [
		{
			field: 'full_name',
			headerName: 'Nom Complet',
			flex: 1,
			filterable: true,
		},
		{
			field: 'email',
			headerName: 'Email',
			flex: 1,
			filterable: true,
		},
		{
			field: 'roles',
			headerName: 'Rôle',
			flex: 1,
			valueGetter: (params) => {
				const role = params.row.user_roles?.[0]?.roles?.name;
				return role || 'Aucun rôle';
			}
		},
		{
			field: 'sectors',
			headerName: 'Secteurs',
			flex: 1,
			valueGetter: (params) => {
				const sectors = params.row.user_sectors?.map(us => us.sectors?.name);
				return sectors?.join(', ') || 'Aucun secteur';
			}
		},
		{
			field: 'created_at',
			headerName: 'Créé le',
			flex: 1,
			filterable: true,
			valueGetter: (params) => formatDate(params.row.created_at),
		},
		{
			field: 'updated_at',
			headerName: 'Mis à jour le',
			flex: 1,
			filterable: true,
			valueGetter: (params) => formatDate(params.row.updated_at),
		},
		{
			field: 'is_active',
			headerName: 'Statut',
			flex: 1,
			filterable: true,
			renderCell: (params) => (
				<Switch
					checked={params.row.is_active}
					onChange={() => handleToggleActive(params.row)}
					color="primary"
				/>
			),
		},
		{
			field: 'actions',
			headerName: 'Actions',
			flex: 1,
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<Box>
					<IconButton
						onClick={() => handleOpenDialog(params.row)}
						color="primary"
						disabled={!params.row.is_active}
					>
						<EditIcon />
					</IconButton>
					<IconButton
						onClick={() => handleDelete(params.row.id)}
						color="error"
						disabled={!params.row.is_active}
					>
						<DeleteIcon />
					</IconButton>
				</Box>
			),
		},
	];

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
				<Typography variant="h4" component="h1">
					Gestion des Utilisateurs
				</Typography>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() => handleOpenDialog()}
				>
					Nouvel Utilisateur
				</Button>
			</Box>

			<Paper sx={{ width: '100%', height: 600 }}>
				<DataGrid
					rows={users}
					columns={columns}
					pageSize={10}
					rowsPerPageOptions={[10, 25, 50]}
					checkboxSelection={false}
					disableSelectionOnClick
					disableRowSelectionOnClick
					localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
					sx={{
						'& .inactive-row': {
							bgcolor: 'rgba(0, 0, 0, 0.04)',
						},
						'& .MuiDataGrid-row:hover': {
							bgcolor: 'transparent',
						},
						'& .MuiDataGrid-row.Mui-selected': {
							bgcolor: 'transparent',
						},
						'& .MuiDataGrid-row.Mui-selected:hover': {
							bgcolor: 'transparent',
						},
					}}
					getRowClassName={(params) =>
						!params.row.is_active ? 'inactive-row' : ''
					}
				/>
			</Paper>


			<Dialog open={openDialog} onClose={handleCloseDialog}>
				<DialogTitle>
					{selectedUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
				</DialogTitle>
				<form onSubmit={handleSubmit}>
					<DialogContent>
						<TextField
							fullWidth
							label="Email"
							type="email"
							margin="normal"
							value={formData.email}
							onChange={(e) => setFormData({ ...formData, email: e.target.value })}
							required
						/>
						<TextField
							fullWidth
							label="Nom Complet"
							margin="normal"
							value={formData.full_name}
							onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
							required
						/>
						<FormControl fullWidth margin="normal">
							<InputLabel>Rôle</InputLabel>
							<Select
								value={formData.role_id}
								onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
								label="Rôle"
							>
								<MenuItem value="">
									<em>Aucun rôle</em>
								</MenuItem>
								{roles.map((role) => (
									<MenuItem key={role.id} value={role.id}>
										{role.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Autocomplete
							multiple
							options={sectors}
							getOptionLabel={(option) => option.name}
							value={formData.sectors}
							onChange={(event, newValue) => {
								setFormData({ ...formData, sectors: newValue });
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Secteurs"
									margin="normal"
									fullWidth
								/>
							)}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip
										label={option.name}
										{...getTagProps({ index })}
									/>
								))
							}
						/>
						<Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
							<Typography>Statut</Typography>
							<Switch
								checked={formData.is_active}
								onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
								color="primary"
								sx={{ ml: 2 }}
							/>
							<Typography sx={{ ml: 1 }}>
								{formData.is_active ? 'Actif' : 'Inactif'}
							</Typography>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseDialog}>Annuler</Button>
						<Button
							type="submit"
							variant="contained"
						>
							{selectedUser ? 'Modifier' : 'Créer'}
						</Button>
					</DialogActions>
				</form>
			</Dialog>

			<Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
				<DialogTitle>Confirmation de suppression</DialogTitle>
				<DialogContent>
					<Typography>
						Êtes-vous sûr de vouloir supprimer cet utilisateur ?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelDelete}>Annuler</Button>
					<Button onClick={handleConfirmDelete} color="error" variant="contained">
						Supprimer
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default UserAdmin;